import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { transactionCall } from "../../api/transactions";

const TYPE_LABEL = {
  BOOKING: "Booking",
  LOCKING: "Locking",
  TTKQ: "TTKQ",
  HDMB: "HĐMB",
};

// Các trường thông tin sản phẩm CRM được phép sửa trước khi duyệt.
const EDITABLE_PRODUCT_FIELDS = [
  "estimatedServiceFeePrice",
  "priceForCommission",
  "transactionRate",
];

const UI = {
  modalBg: "var(--color-backgroundmain)",
  headerBg: "#f3f1ea",
  footerBg: "#f3f1ea",
  sectionCardBg: "#ffffff",
  processBg: "#f8f6ef",
  border: "#d9e2ec",
  borderStrong: "#d8d1c3",
  text: "#1f2d3d",
  subText: "#718096",
  title: "#334155",
  green: "#18a76f",
  greenDark: "#127c55",
  blue: "#2b6cb0",
  blueLight: "#e8f2ff",
  grayLine: "#d8d3c8",
  orangeBg: "#fff6df",
  orangeBorder: "#f2b94b",
  orangeText: "#9a5a00",
  buttonGradient: "linear-gradient(90deg, #22b8cf 0%, #25c97a 100%)",
};

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${n.toLocaleString("vi-VN")} đ`;
};

const percent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${n}%`;
};

const valueText = (value) => {
  if (value === 0) return 0;
  return value || "-";
};

// ISO date -> yyyy-mm-dd cho <input type="date">
const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const getResponseData = (response) =>
  response?.data?.data || response?.data || response;

const getStepStatusLabel = (step) => {
  return step?.label || step?.statusLabel || "-";
};

const getCurrentStatusLabel = (summary) => {
  const label =
    summary?.currentStep?.label || summary?.currentStep?.statusLabel || "";
  if (!label) return "";
  if (label === "Đang xử lý") return "Đang chuyển đổi";
  return label;
};

const stepState = (step) => {
  if (step?.isApproved || step?.status === "APPROVED") return "approved";
  if (step?.status === "SKIPPED" || step?.isSkipped) return "skipped";
  if (step?.isRejected) return "rejected";
  if (step?.isProcessing) return "processing";
  return "default";
};

const isLineActive = (steps, index) => {
  const current = steps[index];
  const nextSteps = steps.slice(index + 1);
  const currentState = stepState(current);

  const hasReachedLaterStep = nextSteps.some((step) => {
    const state = stepState(step);
    return (
      state === "approved" || state === "processing" || state === "rejected"
    );
  });

  return (
    hasReachedLaterStep &&
    (currentState === "approved" ||
      currentState === "skipped" ||
      currentState === "processing")
  );
};

const InfoLabel = ({ children }) => (
  <Text fontSize="12px" color={UI.subText} fontWeight="600" mb="4px">
    {children}
  </Text>
);

const InfoValue = ({ children, color = UI.text, fontSize = "14px" }) => (
  <Text
    fontSize={fontSize}
    fontWeight="700"
    color={color}
    lineHeight="20px"
    wordBreak="break-word"
  >
    {valueText(children)}
  </Text>
);

const SectionTitle = ({ children, mb = "10px" }) => (
  <Text
    fontSize="12px"
    fontWeight="600"
    color={UI.title}
    mb={mb}
    textTransform="uppercase"
    letterSpacing="0.2px"
  >
    {children}
  </Text>
);

const Card = ({ children, ...props }) => (
  <Box
    bg={UI.sectionCardBg}
    border="1px solid"
    borderColor={UI.border}
    rounded="12px"
    p="16px"
    boxShadow="0 1px 2px rgba(15, 23, 42, 0.03)"
    {...props}
  >
    {children}
  </Box>
);

const StatusBadge = ({ label }) => {
  if (!label) return null;

  return (
    <Badge
      bg="#dff7ea"
      color={UI.greenDark}
      rounded="full"
      px="10px"
      py="4px"
      fontSize="11px"
      textTransform="none"
      fontWeight="700"
    >
      {label}
    </Badge>
  );
};

const StepCircle = ({ step }) => {
  const state = stepState(step);

  if (state === "approved") {
    return (
      <Center
        w="38px"
        h="38px"
        rounded="full"
        bg={UI.green}
        color="white"
        boxShadow="0 6px 14px rgba(24, 167, 111, 0.22)"
      >
        <CheckIcon boxSize={3} />
      </Center>
    );
  }

  if (state === "processing") {
    return (
      <Center
        w="38px"
        h="38px"
        rounded="full"
        bg={UI.blueLight}
        border="2px solid"
        borderColor="#3182ce"
        color="#3182ce"
        fontWeight="600"
        fontSize="14px"
      >
        ●
      </Center>
    );
  }

  if (state === "rejected") {
    return (
      <Center
        w="38px"
        h="38px"
        rounded="full"
        bg="#fff5f5"
        border="2px solid"
        borderColor="#e53e3e"
        color="#e53e3e"
        fontWeight="600"
        fontSize="14px"
      >
        {step?.index}
      </Center>
    );
  }

  return (
    <Center
      w="38px"
      h="38px"
      rounded="full"
      bg="white"
      border="2px solid"
      borderColor="#cfc7b9"
      color="#7d7465"
      fontWeight="600"
      fontSize="14px"
    >
      {step?.index}
    </Center>
  );
};

const StepProcess = ({ steps = [], activeType }) => {
  if (!steps.length) return null;

  return (
    <Card bg={UI.processBg} borderColor={UI.borderStrong} p="18px">
      <Text fontSize="12px" fontWeight="600" color="#4d5563" mb="18px">
        QUY TRÌNH GIAO DỊCH
      </Text>

      <Grid templateColumns={`repeat(${steps.length}, minmax(0, 1fr))`} gap="0">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          const state = stepState(step);
          const approved = state === "approved";
          const skipped = state === "skipped";
          const lineActive = isLineActive(steps, index);
          const isActiveType = activeType && step.type === activeType;

          return (
            <GridItem key={step.key} position="relative">
              <Flex
                direction="column"
                align="center"
                justify="flex-start"
                minH="106px"
                px="8px"
                position="relative"
              >
                {!isLast ? (
                  <Box
                    position="absolute"
                    top="18px"
                    left="calc(50% + 28px)"
                    right="-50%"
                    h="2px"
                    bg={lineActive ? UI.green : UI.grayLine}
                    zIndex={0}
                  />
                ) : null}

                <Box position="relative" zIndex={1}>
                  <StepCircle step={step} />
                </Box>

                <Text
                  mt="10px"
                  fontSize="12px"
                  fontWeight="600"
                  color={approved ? UI.greenDark : UI.text}
                  textAlign="center"
                >
                  {step.title}
                </Text>

                {step?.dateText ? (
                  <Text
                    mt="2px"
                    fontSize="12px"
                    color={approved ? UI.greenDark : UI.subText}
                    fontWeight="700"
                    textAlign="center"
                  >
                    {step.dateText}
                  </Text>
                ) : (
                  <Box h="20px" />
                )}

                <Text
                  mt="1px"
                  fontSize="11px"
                  fontWeight="600"
                  textAlign="center"
                  color={
                    approved
                      ? UI.greenDark
                      : state === "processing"
                        ? UI.blue
                        : state === "rejected"
                          ? "#c53030"
                          : skipped
                            ? "#7d7465"
                            : "#6b7280"
                  }
                >
                  {getStepStatusLabel(step)}
                </Text>

                {isActiveType ? (
                  <Badge
                    mt="4px"
                    bg={UI.blueLight}
                    color={UI.blue}
                    rounded="full"
                    px="8px"
                    py="2px"
                    fontSize="10px"
                    textTransform="none"
                    fontWeight="700"
                  >
                    Đang duyệt
                  </Badge>
                ) : null}
              </Flex>
            </GridItem>
          );
        })}
      </Grid>
    </Card>
  );
};

const WarningBox = ({ warning }) => {
  if (!warning) return null;

  const items = Array.isArray(warning.items) ? warning.items : [];

  return (
    <Box>
      <SectionTitle>THÔNG BÁO</SectionTitle>

      <Alert
        status="warning"
        rounded="12px"
        border="1px solid"
        borderColor={UI.orangeBorder}
        bg={UI.orangeBg}
        color={UI.orangeText}
        alignItems="flex-start"
        px="16px"
        py="14px"
      >
        <AlertIcon mt="2px" color="#d88a00" />

        <Box>
          <Text fontWeight="600" fontSize="14px">
            {warning.title}
          </Text>

          {items.length ? (
            <VStack align="stretch" spacing="2px" mt="4px">
              {items.map((item) => (
                <Text key={item.field} fontSize="12px" fontWeight="500">
                  - {item.message}
                </Text>
              ))}
            </VStack>
          ) : (
            <Text fontSize="12px" mt="3px" fontWeight="500">
              {warning.description}
            </Text>
          )}
        </Box>
      </Alert>
    </Box>
  );
};

const TypeBadge = ({ children }) => (
  <Badge
    rounded="full"
    px="8px"
    py="2px"
    bg="#e8f2ff"
    color={UI.blue}
    fontSize="10px"
    textTransform="none"
  >
    {children}
  </Badge>
);

const HistoryTable = ({ history = [] }) => (
  <Box
    border="1px solid"
    borderColor={UI.borderStrong}
    rounded="12px"
    overflow="hidden"
    bg="white"
  >
    <Table size="sm">
      <Thead bg={UI.headerBg}>
        <Tr>
          <Th
            color="#4d5563"
            fontSize="12px"
            textTransform="none"
            py="10px"
            w="150px"
          >
            Thời gian
          </Th>
          <Th color="#4d5563" fontSize="12px" textTransform="none" py="10px">
            Hành động
          </Th>
          <Th
            color="#4d5563"
            fontSize="12px"
            textTransform="none"
            py="10px"
            w="170px"
          >
            Nhân sự
          </Th>
        </Tr>
      </Thead>

      <Tbody>
        {!history.length ? (
          <Tr>
            <Td colSpan={3}>
              <Center py={4} color="gray.500" fontSize="12px">
                Chưa có lịch sử giao dịch
              </Center>
            </Td>
          </Tr>
        ) : (
          history.map((item) => (
            <Tr key={item._id}>
              <Td py="10px" color="#2d3748">
                {item.timeText || "-"}
              </Td>

              <Td py="10px">
                <HStack spacing={2}>
                  <Text fontWeight="600" color={UI.text}>
                    {item.actionLabel}
                  </Text>
                  <TypeBadge>{item.typeLabel}</TypeBadge>
                </HStack>
              </Td>

              <Td py="10px" color="#2d3748" fontWeight="500">
                {item.employee?.fullName || item.employee?.name || "-"}
              </Td>
            </Tr>
          ))
        )}
      </Tbody>
    </Table>
  </Box>
);

const EditNumberField = ({ value, onChange, suffix }) => (
  <Flex align="center" gap="6px">
    <Input
      type="number"
      size="sm"
      value={value === null || value === undefined ? "" : value}
      onChange={(e) => onChange(e.target.value)}
      bg="white"
      borderColor={UI.border}
      rounded="8px"
      fontSize="14px"
      fontWeight="600"
      _focus={{ borderColor: UI.blue, boxShadow: `0 0 0 1px ${UI.blue}` }}
    />
    {suffix ? (
      <Text fontSize="13px" color={UI.subText} fontWeight="600">
        {suffix}
      </Text>
    ) : null}
  </Flex>
);

const ProductInfo = ({
  product = {},
  isEditing = false,
  form = {},
  onChange,
  showTransactionDate = false,
  transactionDateText = "",
}) => (
  <Card>
    <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="16px 36px">
      <GridItem>
        <InfoLabel>Mã căn</InfoLabel>
        <InfoValue fontSize="15px">{product.code || product.name}</InfoValue>
      </GridItem>

      <GridItem>
        <InfoLabel>Giá niêm yết</InfoLabel>
        <InfoValue fontSize="15px">{money(product.totalValue)}</InfoValue>
      </GridItem>

      <GridItem>
        <InfoLabel>Giá tính phí DV tạm tính</InfoLabel>
        {isEditing ? (
          <EditNumberField
            value={form.estimatedServiceFeePrice}
            onChange={(v) => onChange("estimatedServiceFeePrice", v)}
            suffix="đ"
          />
        ) : (
          <InfoValue color={UI.blue} fontSize="15px">
            {money(product.estimatedServiceFeePrice)}
          </InfoValue>
        )}
      </GridItem>

      <GridItem>
        <InfoLabel>Giá tính phí dịch vụ</InfoLabel>
        {isEditing ? (
          <EditNumberField
            value={form.priceForCommission}
            onChange={(v) => onChange("priceForCommission", v)}
            suffix="đ"
          />
        ) : (
          <InfoValue color={UI.blue} fontSize="15px">
            {money(product.priceForCommission)}
          </InfoValue>
        )}
      </GridItem>

      <GridItem>
        <InfoLabel>Tỉ lệ nhận về</InfoLabel>
        {isEditing ? (
          <EditNumberField
            value={form.transactionRate}
            onChange={(v) => onChange("transactionRate", v)}
            suffix="%"
          />
        ) : (
          <Badge
            bg="#dff3cf"
            color="#3c811f"
            rounded="full"
            px="12px"
            py="4px"
            fontSize="15px"
            fontWeight="600"
          >
            {percent(product.transactionRate)}
          </Badge>
        )}
      </GridItem>

      {showTransactionDate ? (
        <GridItem>
          <InfoLabel>Ngày cọc</InfoLabel>
          <InfoValue fontSize="15px">{transactionDateText || "-"}</InfoValue>
        </GridItem>
      ) : null}
    </Grid>
  </Card>
);

const ProjectInfo = ({
  project = {},
  saleOpeningEvent = {},
  isEditing = false,
  projectForm = {},
  saleEventForm = {},
  onProjectChange,
  onSaleEventChange,
}) => (
  <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="14px">
    <Card minH="110px">
      <InfoLabel>Dự án</InfoLabel>

      {isEditing ? (
        <VStack align="stretch" spacing="8px">
          <Input
            size="sm"
            placeholder="Tên dự án"
            value={projectForm.name ?? ""}
            onChange={(e) => onProjectChange("name", e.target.value)}
            bg="white"
            borderColor={UI.border}
            rounded="8px"
          />
          <Input
            size="sm"
            placeholder="Địa chỉ"
            value={projectForm.address ?? ""}
            onChange={(e) => onProjectChange("address", e.target.value)}
            bg="white"
            borderColor={UI.border}
            rounded="8px"
          />
        </VStack>
      ) : (
        <>
          <InfoValue fontSize="15px">{project.name}</InfoValue>

          <Text fontSize="12px" color="#4a5568" mt="8px" fontWeight="500">
            {project.address || "-"}
          </Text>

          {project.investor ? (
            <Text fontSize="12px" color="#4a5568" mt="4px" fontWeight="500">
              Chủ đầu tư: {project.investor}
            </Text>
          ) : null}
        </>
      )}
    </Card>

    <Card minH="110px">
      <InfoLabel>Đợt mở bán</InfoLabel>

      {isEditing ? (
        <VStack align="stretch" spacing="8px">
          <Input
            size="sm"
            placeholder="Tên đợt mở bán"
            value={saleEventForm.name ?? ""}
            onChange={(e) => onSaleEventChange("name", e.target.value)}
            bg="white"
            borderColor={UI.border}
            rounded="8px"
          />
          <Flex align="center" gap="8px">
            <Text fontSize="12px" color="#4a5568" minW="84px">
              Ngày mở bán
            </Text>
            <Input
              size="sm"
              type="date"
              value={saleEventForm.startTime ?? ""}
              onChange={(e) => onSaleEventChange("startTime", e.target.value)}
              bg="white"
              borderColor={UI.border}
              rounded="8px"
            />
          </Flex>
          <Flex align="center" gap="8px">
            <Text fontSize="12px" color="#4a5568" minW="84px">
              Giá
            </Text>
            <Input
              size="sm"
              type="number"
              placeholder="Giá"
              value={saleEventForm.price ?? ""}
              onChange={(e) => onSaleEventChange("price", e.target.value)}
              bg="white"
              borderColor={UI.border}
              rounded="8px"
            />
          </Flex>

          <Text fontSize="12px" color="#9aa1ab" fontWeight="500">
            Hệ số K: {saleOpeningEvent.kRateText || "-"} (không sửa)
          </Text>
        </VStack>
      ) : (
        <>
          <InfoValue fontSize="15px">{saleOpeningEvent.name}</InfoValue>

          <Text fontSize="12px" color="#4a5568" mt="8px" fontWeight="500">
            Ngày mở bán: {saleOpeningEvent.startTimeText || "-"}
          </Text>

          <Text fontSize="12px" color="#4a5568" mt="4px" fontWeight="500">
            Hệ số K: {saleOpeningEvent.kRateText || "-"}
          </Text>
        </>
      )}
    </Card>
  </Grid>
);

const CustomerInfo = ({
  customer = {},
  isEditing = false,
  form = {},
  onChange,
}) => (
  <Card>
    <HStack spacing="14px" align="center">
      <Avatar
        name={customer.name}
        bg="#ece7ff"
        color="#6952d5"
        fontWeight="600"
      />

      <Grid templateColumns="1.2fr 1.5fr 1fr" gap="18px" flex="1">
        <GridItem minW={0}>
          <InfoLabel>Họ và tên</InfoLabel>
          {isEditing ? (
            <Input
              size="sm"
              value={form.name ?? ""}
              onChange={(e) => onChange("name", e.target.value)}
              bg="white"
              borderColor={UI.border}
              rounded="8px"
            />
          ) : (
            <InfoValue>{customer.name}</InfoValue>
          )}
        </GridItem>

        <GridItem minW={0}>
          <InfoLabel>Email</InfoLabel>
          {isEditing ? (
            <Input
              size="sm"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => onChange("email", e.target.value)}
              bg="white"
              borderColor={UI.border}
              rounded="8px"
            />
          ) : (
            <InfoValue color={UI.blue}>{customer.email}</InfoValue>
          )}
        </GridItem>

        <GridItem minW={0}>
          <InfoLabel>Số điện thoại</InfoLabel>
          {isEditing ? (
            <Input
              size="sm"
              value={form.phone ?? ""}
              onChange={(e) => onChange("phone", e.target.value)}
              bg="white"
              borderColor={UI.border}
              rounded="8px"
            />
          ) : (
            <InfoValue>{customer.phone}</InfoValue>
          )}
        </GridItem>
      </Grid>
    </HStack>
  </Card>
);

const CreatorInfo = ({ creator = {} }) => (
  <Card>
    <HStack spacing="12px" align="center">
      <Avatar
        name={creator.fullName}
        bg="#dff8ee"
        color={UI.greenDark}
        fontWeight="600"
      />

      <Box minW={0}>
        <InfoValue>{creator.fullName}</InfoValue>
        <Text fontSize="12px" color="#4a5568" fontWeight="500">
          Mã NV: {creator.code || "-"}
        </Text>
      </Box>
    </HStack>

    <Divider my="14px" borderColor={UI.border} />

    <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="16px 28px">
      <GridItem minW={0}>
        <InfoLabel>Vùng</InfoLabel>
        <InfoValue>{creator.saleArea}</InfoValue>
      </GridItem>

      <GridItem minW={0}>
        <InfoLabel>Khối</InfoLabel>
        <InfoValue>{creator.saleBlock}</InfoValue>
      </GridItem>

      <GridItem minW={0}>
        <InfoLabel>Giám đốc khối</InfoLabel>
        <InfoValue>{creator.saleBlockManager}</InfoValue>
      </GridItem>

      <GridItem minW={0}>
        <InfoLabel>Giám đốc chi nhánh</InfoLabel>
        <InfoValue>{creator.saleAreaManager}</InfoValue>
      </GridItem>
    </Grid>
  </Card>
);

const ParticipantTable = ({
  participants = [],
  isEditing = false,
  form = {},
  onChange,
}) => {
  const total = useMemo(
    () =>
      participants.reduce((sum, item) => {
        const edited = form[item._id];
        const rate =
          isEditing && edited !== undefined && edited !== ""
            ? edited
            : item.commissionRate;
        return sum + Number(rate || 0);
      }, 0),
    [participants, isEditing, form],
  );

  return (
    <Box
      border="1px solid"
      borderColor={UI.borderStrong}
      rounded="12px"
      overflow="hidden"
      bg="white"
    >
      <Table size="sm">
        <Thead bg={UI.headerBg}>
          <Tr>
            <Th color="#4d5563" fontSize="12px" textTransform="none" py="10px">
              Họ tên
            </Th>
            <Th color="#4d5563" fontSize="12px" textTransform="none" py="10px">
              Mã nhân viên
            </Th>
            <Th
              color="#4d5563"
              fontSize="12px"
              textTransform="none"
              py="10px"
              isNumeric
            >
              Tỉ lệ GD
            </Th>
          </Tr>
        </Thead>

        <Tbody>
          {!participants.length ? (
            <Tr>
              <Td colSpan={3}>
                <Center py={4} color="gray.500" fontSize="12px">
                  Chưa có nhân sự tham gia giao dịch
                </Center>
              </Td>
            </Tr>
          ) : (
            participants.map((item, index) => (
              <Tr key={item._id || item.employeeId || index}>
                <Td py="10px">
                  <HStack spacing="10px">
                    <Avatar
                      size="sm"
                      name={item.fullName}
                      bg={index % 2 === 0 ? "#dff8ee" : "#ece7ff"}
                      color={index % 2 === 0 ? UI.greenDark : "#6952d5"}
                      fontWeight="600"
                    />
                    <Text fontWeight="700" color={UI.text}>
                      {item.fullName || "-"}
                    </Text>
                  </HStack>
                </Td>

                <Td py="10px" color="#2d3748" fontWeight="500">
                  {item.code || "-"}
                </Td>

                <Td py="10px" isNumeric color={UI.greenDark} fontWeight="600">
                  {isEditing ? (
                    <Flex align="center" justify="flex-end" gap="4px">
                      <Input
                        size="sm"
                        type="number"
                        w="80px"
                        textAlign="right"
                        value={
                          form[item._id] !== undefined
                            ? form[item._id]
                            : (item.commissionRate ?? "")
                        }
                        onChange={(e) => onChange(item._id, e.target.value)}
                        bg="white"
                        borderColor={UI.border}
                        rounded="8px"
                      />
                      <Text>%</Text>
                    </Flex>
                  ) : (
                    percent(item.commissionRate)
                  )}
                </Td>
              </Tr>
            ))
          )}

          <Tr bg={UI.headerBg}>
            <Td colSpan={2} textAlign="right" fontWeight="600">
              Tổng:
            </Td>

            <Td isNumeric color={UI.greenDark} fontWeight="600">
              {percent(total)}
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
};

const TransactionDetailModal = ({
  isOpen,
  onClose,
  id,
  type,
  onConfirm,
  confirmLoading,
}) => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Ngày giao dịch (ngày cọc) cho bước duyệt HĐMB/TTKQ. Mặc định lấy bookingTime.
  const [transactionDate, setTransactionDate] = useState("");

  // Các bước duyệt có chọn ngày cọc (duyệt luôn bước 1 & 2 phí dịch vụ).
  const showDepositDate = type === "HDMB" || type === "TTKQ";

  const fetchDetail = useCallback(async () => {
    if (!isOpen || !id || !type) return;

    setLoading(true);

    try {
      const response = await transactionCall.getDetail({ id, type });
      setDetail(getResponseData(response));
    } finally {
      setLoading(false);
    }
  }, [isOpen, id, type]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Mặc định điền Ngày cọc = transactionDate (nếu có) hoặc bookingTime của locking.
  useEffect(() => {
    if (type !== "HDMB" && type !== "TTKQ") return;
    const s = detail?.summary || {};
    setTransactionDate(toDateInputValue(s.transactionDate || s.bookingTime));
  }, [detail, type]);

  const summary = detail?.summary || {};
  const currentStatusLabel = getCurrentStatusLabel(summary);
  const typeLabel = TYPE_LABEL[type] || "giao dịch";

  // Ngày cọc dạng text dd/mm/yyyy để hiển thị ở khối THÔNG TIN SẢN PHẨM.
  const transactionDateText = transactionDate
    ? transactionDate.split("-").reverse().join("/")
    : "";

  const handleClose = () => {
    setDetail(null);
    setIsEditing(false);
    setEditForm({});
    setTransactionDate("");
    onClose();
  };

  const blankForm = () => ({
    product: {},
    customer: {},
    project: {},
    saleOpeningEvent: {},
    participants: {},
  });

  const startEditing = () => {
    const product = summary.product || {};
    const customer = summary.customer || {};
    const project = summary.project || {};
    const saleOpeningEvent = summary.saleOpeningEvent || {};

    const orEmpty = (value) =>
      value === null || value === undefined ? "" : value;

    setEditForm({
      product: EDITABLE_PRODUCT_FIELDS.reduce((acc, field) => {
        acc[field] = orEmpty(product[field]);
        return acc;
      }, {}),
      customer: {
        name: orEmpty(customer.name),
        phone: orEmpty(customer.phone),
        email: orEmpty(customer.email),
      },
      project: {
        name: orEmpty(project.name),
        address: orEmpty(project.address),
      },
      saleOpeningEvent: {
        name: orEmpty(saleOpeningEvent.name),
        startTime: toDateInputValue(saleOpeningEvent.startTime),
        price: orEmpty(saleOpeningEvent.price),
      },
      participants: {},
    });

    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm(blankForm());
  };

  const handleSectionChange = (section, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [field]: value },
    }));
  };

  const handleParticipantChange = (participantId, value) => {
    setEditForm((prev) => ({
      ...prev,
      participants: { ...(prev.participants || {}), [participantId]: value },
    }));
  };

  // Chỉ gửi các trường có giá trị; số rỗng -> bỏ qua.
  const buildSectionPayload = (form, numericFields = []) => {
    const out = {};
    Object.keys(form || {}).forEach((field) => {
      const value = form[field];
      if (value === "" || value === null || value === undefined) return;
      out[field] = numericFields.includes(field) ? Number(value) : value;
    });
    return out;
  };

  const handleSave = async () => {
    const form = editForm || {};
    const payload = {};

    if (summary.product?._id) {
      const product = buildSectionPayload(form.product, EDITABLE_PRODUCT_FIELDS);
      if (Object.keys(product).length) payload.product = product;
    }

    if (summary.customer?._id) {
      const customer = buildSectionPayload(form.customer);
      if (Object.keys(customer).length) payload.customer = customer;
    }

    if (summary.project?._id) {
      const project = buildSectionPayload(form.project);
      if (Object.keys(project).length) payload.project = project;
    }

    if (summary.saleOpeningEvent?._id) {
      const saleOpeningEvent = buildSectionPayload(form.saleOpeningEvent, [
        "price",
      ]);
      if (Object.keys(saleOpeningEvent).length) {
        payload.saleOpeningEvent = saleOpeningEvent;
      }
    }

    // Chỉ locking mới có nhân sự tham gia.
    const participantEdits = Object.entries(form.participants || {})
      .filter(([, v]) => v !== "" && v !== null && v !== undefined)
      .map(([participantId, v]) => ({
        _id: participantId,
        commissionRate: Number(v),
      }));

    if (participantEdits.length) payload.participants = participantEdits;

    if (!Object.keys(payload).length) {
      toast({
        title: "Chưa có thông tin để lưu",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);

    try {
      await transactionCall.updateDetail({ id, type, data: payload });

      toast({
        title: "Đã lưu thông tin giao dịch",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      setIsEditing(false);
      setEditForm(blankForm());
      await fetchDetail();
    } catch (error) {
      console.log("save transaction detail error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!onConfirm) return;

    await onConfirm({
      id,
      type,
      detail,
      // Chỉ HĐMB/TTKQ mới gửi ngày cọc; type khác giữ nguyên hành vi cũ.
      transactionDate: showDepositDate ? transactionDate : undefined,
      refetch: fetchDetail,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.420" />

      <ModalContent
        rounded="16px"
        overflow="hidden"
        maxW="920px"
        w="calc(100vw - 64px)"
        my="24px"
        bg={UI.modalBg}
        boxShadow="0 20px 50px rgba(15, 23, 42, 0.22)"
      >
        <ModalHeader
          bg={UI.headerBg}
          borderBottom="1px solid"
          borderColor={UI.borderStrong}
          py="14px"
          px="18px"
        >
          <Flex align="center" justify="space-between">
            <HStack spacing="10px">
              <Box w="9px" h="9px" rounded="full" bg={UI.green} />

              <Text fontSize="16px" fontWeight="600" color={UI.text}>
                Chi tiết giao dịch
              </Text>

              <StatusBadge label={currentStatusLabel} />
            </HStack>

            <HStack spacing="8px">
              {!loading && detail ? (
                isEditing ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="#cfc7b9"
                      color="#344054"
                      onClick={cancelEditing}
                      isDisabled={saving}
                    >
                      Hủy
                    </Button>

                    <Button
                      size="sm"
                      bg={UI.green}
                      color="white"
                      _hover={{ filter: "brightness(95%)" }}
                      onClick={handleSave}
                      isLoading={saving}
                    >
                      Lưu thay đổi
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    borderColor={UI.blue}
                    color={UI.blue}
                    _hover={{ bg: UI.blueLight }}
                    onClick={startEditing}
                  >
                    Sửa thông tin
                  </Button>
                )
              ) : null}

              <IconButton
                icon={<CloseIcon boxSize={3} />}
                size="md"
                variant="ghost"
                onClick={handleClose}
                aria-label="close"
                color="#566173"
                _hover={{ bg: "#ebe7dd" }}
              />
            </HStack>
          </Flex>
        </ModalHeader>

        <ModalBody px="18px" py="16px" bg={UI.modalBg}>
          {loading ? (
            <Center py={24}>
              <Spinner color={UI.green} thickness="3px" />
            </Center>
          ) : (
            <VStack align="stretch" spacing="18px">
              <WarningBox warning={summary.warning} />

              <Box>
                <SectionTitle>LỊCH SỬ GIAO DỊCH</SectionTitle>

                <VStack align="stretch" spacing="12px">
                  <StepProcess steps={summary.steps || []} activeType={type} />
                  <HistoryTable history={summary.history || []} />
                </VStack>
              </Box>

              <Box>
                <SectionTitle>THÔNG TIN SẢN PHẨM</SectionTitle>
                <ProductInfo
                  product={summary.product || {}}
                  isEditing={isEditing}
                  form={editForm.product || {}}
                  onChange={(field, value) =>
                    handleSectionChange("product", field, value)
                  }
                  showTransactionDate={showDepositDate}
                  transactionDateText={transactionDateText}
                />
              </Box>

              {showDepositDate ? (
                <Box>
                  <SectionTitle>THÔNG TIN GIAO DỊCH</SectionTitle>
                  <Card>
                    <Box maxW="260px">
                      <InfoLabel>Ngày cọc</InfoLabel>
                      <Input
                        type="date"
                        size="sm"
                        value={transactionDate}
                        onChange={(e) => setTransactionDate(e.target.value)}
                        isDisabled={isEditing}
                        bg="white"
                        borderColor={UI.border}
                        rounded="8px"
                        fontSize="14px"
                        fontWeight="600"
                        _focus={{
                          borderColor: UI.blue,
                          boxShadow: `0 0 0 1px ${UI.blue}`,
                        }}
                      />
                      <Text fontSize="11px" color={UI.subText} mt="6px">
                        Lưu khi xác nhận {typeLabel}.
                      </Text>
                    </Box>
                  </Card>
                </Box>
              ) : null}

              <Box>
                <SectionTitle>THÔNG TIN DỰ ÁN & ĐỢT MỞ BÁN</SectionTitle>
                <ProjectInfo
                  project={summary.project || {}}
                  saleOpeningEvent={summary.saleOpeningEvent || {}}
                  isEditing={isEditing}
                  projectForm={editForm.project || {}}
                  saleEventForm={editForm.saleOpeningEvent || {}}
                  onProjectChange={(field, value) =>
                    handleSectionChange("project", field, value)
                  }
                  onSaleEventChange={(field, value) =>
                    handleSectionChange("saleOpeningEvent", field, value)
                  }
                />
              </Box>

              <Box>
                <SectionTitle>THÔNG TIN KHÁCH HÀNG</SectionTitle>
                <CustomerInfo
                  customer={summary.customer || {}}
                  isEditing={isEditing}
                  form={editForm.customer || {}}
                  onChange={(field, value) =>
                    handleSectionChange("customer", field, value)
                  }
                />
              </Box>

              <Box>
                <SectionTitle>NHÂN VIÊN TẠO GIAO DỊCH</SectionTitle>
                <CreatorInfo creator={summary.creator || {}} />
              </Box>

              <Box>
                <SectionTitle>NHÂN SỰ THAM GIA GIAO DỊCH</SectionTitle>
                <ParticipantTable
                  participants={summary.participants || []}
                  isEditing={isEditing}
                  form={editForm.participants || {}}
                  onChange={handleParticipantChange}
                />
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter
          bg={UI.footerBg}
          borderTop="1px solid"
          borderColor={UI.borderStrong}
          py="12px"
          px="18px"
        >
          <Flex align="center" justify="space-between" w="100%">
            <HStack spacing="6px">
              <Text fontSize="12px" color={UI.subText} fontWeight="600">
                Đang duyệt bước:
              </Text>
              <Badge
                bg={UI.blueLight}
                color={UI.blue}
                rounded="full"
                px="10px"
                py="3px"
                fontSize="12px"
                textTransform="none"
                fontWeight="700"
              >
                {typeLabel}
              </Badge>
            </HStack>

            <HStack spacing="10px">
              <Button
                onClick={handleClose}
                p={"10px 16px 10px 12px"}
                h={"44px"}
                bg="white"
                border="1px solid"
                borderColor="#cfc7b9"
                color="#344054"
                _hover={{ bg: "#fafafa" }}
              >
                <Text fontSize={{ base: "15px" }} fontWeight={600}>
                  Đóng
                </Text>
              </Button>

              <Button
                display={"flex"}
                alignItems={"center"}
                p={"10px 16px 10px 12px"}
                gap={"8px"}
                h={"44px"}
                backgroundImage="var(--linear-gradient-excel)"
                _hover={{
                  filter: "brightness(90%)",
                }}
                onClick={handleConfirm}
                isLoading={confirmLoading}
                isDisabled={!onConfirm || loading || isEditing}
              >
                <Text
                  fontSize={{ base: "15px" }}
                  fontWeight={600}
                  color="var(--color-main)"
                >
                  Xác nhận {typeLabel}
                </Text>
              </Button>
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TransactionDetailModal;
